import { useEffect, useState, type FormEvent } from "react";
import { useNavigate } from "react-router";
import { api, ApiError, errorMessage, unwrap, type EventData, type EventHeader, type EventSettings, type GlobalSettings, type Polygon, type PublishChecklist, type Schemas } from "../../api/client";
import { useSession } from "../../auth/session";
import { appliesTo, CONFIRMATION_MIN_GAIN_MIN_MAX, confirmationOverrideCount, FLAG_KEY, globalConfirmationNumber, globalConfirmations, MODES, overrideOf, PASSAGE_KINDS, REOFFER_ABS_MIN_MAX, REOFFER_REL_PCT_MAX, withOverride, type ConfirmationNumberKey } from "../../domain/confirmation";
import { formatDateTime, formatDuration, fromParisInput, toParisInput } from "../../domain/format";
import { COURSE_CROSSING_S_MAX, COURSE_CROSSING_S_STEP, COURSE_DEFAULTS, COURSE_RELATIVE_PCT_MAX, COURSE_TOLERANCE_MIN_MAX, crossingPenaltyS, minutesToSeconds, secondsToMinutes, thresholdS, TOLERANCE_MIN_MAX, TOLERANCE_MIN_STEP, type CourseSettingKey } from "../../domain/tolerance";
import { fr } from "../../i18n/fr";
import { Confirm, Field, Notice, useOffline } from "../../ui/components";

interface Props {
  eventId: string; event: EventData; activeNow: boolean;
  /** administrator defaults in force, shown next to each inherited value (SW-PA-01) */
  global: GlobalSettings | null;
  zonePending: Polygon | null; zoneDrawing: boolean;
  onDrawZone(): void; onZoneApplied(): void; onCancelZone(): void;
  onChanged(msg?: string): Promise<void>; onDeleted(): void;
}

const OPTION_KEYS = ["busLanes", "pedestrianZones", "emergencyAccess", "wrongWay", "crossableBarriers", "courses"] as const;
type OptionKey = (typeof OPTION_KEYS)[number];
const COURSE_KEYS = Object.keys(COURSE_DEFAULTS) as CourseSettingKey[];

/** Identity, period, zone, option overrides and life cycle of the event (SW-WS-20 to SW-WS-26). */
export function SettingsPanel({ eventId, event: e, activeNow, global, zonePending, zoneDrawing, onDrawZone, onZoneApplied, onCancelZone, onChanged, onDeleted }: Props) {
  const s = useSession();
  const navigate = useNavigate();
  const offline = useOffline();
  const readOnly = e.status === "ARCHIVED" || !s.can("event.write");
  const [name, setName] = useState(e.name);
  const [description, setDescription] = useState(e.description ?? "");
  const [start, setStart] = useState(toParisInput(e.startAt));
  const [end, setEnd] = useState(toParisInput(e.endAt));
  const [settings, setSettings] = useState<EventSettings>(e.settings ?? {});
  const [error, setError] = useState<string | null>(null);
  const [conflict, setConflict] = useState<EventHeader | null>(null);
  const [busy, setBusy] = useState(false);
  const [checklist, setChecklist] = useState<PublishChecklist | null>(null);
  const [confirm, setConfirm] = useState<"publish" | "archive" | "unarchive" | "delete" | "clearZone" | null>(null);
  const [confirmName, setConfirmName] = useState("");

  // the server state wins when it changes (another operator)
  useEffect(() => { setName(e.name); setDescription(e.description ?? ""); setStart(toParisInput(e.startAt)); setEnd(toParisInput(e.endAt)); setSettings(e.settings ?? {}); }, [e.version]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!s.can("event.publish") || e.status !== "DRAFT") { setChecklist(null); return; }
    api.GET("/events/{id}/checklist", { params: { path: { id: eventId } } }).then((r) => setChecklist(unwrap(r))).catch(() => setChecklist(null));
  }, [eventId, e.revision, e.status, s]);

  const patch = async (body: Partial<Schemas["EventPatchRequest"]>, msg: string) => {
    setBusy(true); setError(null); setConflict(null);
    try {
      unwrap(await api.PATCH("/events/{id}", { params: { path: { id: eventId } }, body: { version: e.version ?? 0, clearZone: false, ...body } }));
      await onChanged(msg);
    } catch (x) {
      if (x instanceof ApiError && x.code === "VERSION_CONFLICT" && x.current) setConflict(x.current as EventHeader);
      setError(errorMessage(x, fr.errors));
    } finally { setBusy(false); }
  };

  const saveIdentity = (ev: FormEvent) => {
    ev.preventDefault();
    const startAt = fromParisInput(start), endAt = fromParisInput(end);
    if (Date.parse(endAt) <= Date.parse(startAt)) { setError(fr.events.endBeforeStart); return; }
    void patch({ name: name.trim(), description, startAt, endAt, settings }, fr.eventSettings.saved);
  };

  const lifecycle = async (action: "publish" | "archive" | "unarchive") => {
    setBusy(true); setError(null);
    try {
      if (action === "publish") unwrap(await api.POST("/events/{id}/publish", { params: { path: { id: eventId } } }));
      else if (action === "archive") unwrap(await api.POST("/events/{id}/archive", { params: { path: { id: eventId } } }));
      else unwrap(await api.POST("/events/{id}/unarchive", { params: { path: { id: eventId } } }));
      setConfirm(null);
      await onChanged(action === "publish" ? fr.workspace.published : action === "archive" ? fr.workspace.archived : fr.workspace.unarchived);
    } catch (x) { setError(errorMessage(x, fr.errors)); setConfirm(null); } finally { setBusy(false); }
  };

  const del = async () => {
    setBusy(true); setError(null);
    try {
      unwrap(await api.DELETE("/events/{id}", { params: { path: { id: eventId } }, body: { confirmName } }));
      setConfirm(null);
      onDeleted();
      navigate("/");
    } catch (x) { setError(errorMessage(x, fr.errors)); } finally { setBusy(false); }
  };

  const setOption = (k: OptionKey, v: "inherit" | "on" | "off") => {
    const o = { ...(settings.emergencyOptions ?? {}) } as Record<string, boolean | undefined>;
    if (v === "inherit") delete o[k]; else o[k] = v === "on";
    setSettings({ ...settings, emergencyOptions: o });
  };
  const overriddenCount = Object.values(settings.emergencyOptions ?? {}).filter((v) => v != null).length + (settings.toleranceAbsoluteS != null ? 1 : 0) + (settings.toleranceRelative != null ? 1 : 0)
    + COURSE_KEYS.filter((k) => settings[k] != null).length + confirmationOverrideCount(settings);
  // effective tolerance for the example sentence (SW-PA-03): event value, else global
  const effAbs = settings.toleranceAbsoluteS ?? global?.toleranceAbsoluteS ?? 120;
  const effRel = settings.toleranceRelative ?? global?.toleranceRelative ?? 0.15;
  // the global course settings may be absent (server older than 1.1): the engine defaults apply (chantier Parcours §1.4)
  const globalCourse = (k: CourseSettingKey): number => global?.[k] ?? COURSE_DEFAULTS[k];
  const effCourse = (k: CourseSettingKey): number => settings[k] ?? globalCourse(k);
  const setCourse = (k: CourseSettingKey, v: number | undefined) => setSettings({ ...settings, [k]: v });
  const globalOption = (k: OptionKey) => (global ? ((global.emergencyOptions[k] ?? true) ? fr.eventSettings.on : fr.eventSettings.off).toLowerCase() : null);
  const pct = (v: number) => `${Math.round(v * 100)} %`;
  // confirmation settings (chantier Confirmations §1.2): engine defaults when the global ones are absent (server older than 1.2)
  const globalFlags = globalConfirmations(global);
  const globalConfirmation = (k: ConfirmationNumberKey): number => globalConfirmationNumber(global, k);
  const setConfirmation = (k: ConfirmationNumberKey, v: number | undefined) => setSettings({ ...settings, [k]: v });

  return (
    <section className="stack">
      <form className="card stack form" onSubmit={saveIdentity}>
        <h3>{fr.eventSettings.identity}</h3>
        <Field label={fr.events.name}><input value={name} onChange={(ev) => setName(ev.target.value)} required maxLength={120} disabled={readOnly} /></Field>
        <div className="grid2">
          <Field label={fr.events.start}><input type="datetime-local" value={start} onChange={(ev) => setStart(ev.target.value)} required disabled={readOnly} /></Field>
          <Field label={fr.events.end}><input type="datetime-local" value={end} onChange={(ev) => setEnd(ev.target.value)} required disabled={readOnly} /></Field>
        </div>
        <Field label={fr.events.description}><textarea rows={2} value={description} onChange={(ev) => setDescription(ev.target.value)} disabled={readOnly} /></Field>

        <h3>{fr.eventSettings.options} {overriddenCount > 0 && <span className="badge">{overriddenCount}</span>}</h3>
        <p className="muted small">{fr.eventSettings.optionsHelp}</p>
        {OPTION_KEYS.map((k) => {
          const v = settings.emergencyOptions?.[k];
          const cur = v == null ? "inherit" : v ? "on" : "off";
          return (
            <div className="row between wrap small" key={k}>
              <span>{fr.settingsNames[k]}</span>
              <div className="segmented">
                {(["inherit", "on", "off"] as const).map((o) => (
                  <button type="button" key={o} className={cur === o ? "active" : ""} disabled={readOnly} onClick={() => setOption(k, o)}>
                    {o === "inherit" && globalOption(k) ? fr.eventSettings.inheritValue(globalOption(k)!) : fr.eventSettings[o]}
                  </button>
                ))}
              </div>
            </div>
          );
        })}
        <p className="muted small">{fr.eventSettings.tolerance} : {fr.eventSettings.toleranceHelp}</p>
        <div className="grid2">
          <Field label={fr.eventSettings.toleranceAbs} hint={global ? fr.eventSettings.globalValue(formatDuration(global.toleranceAbsoluteS)) : fr.eventSettings.inherit}>
            <input type="number" min={0} max={TOLERANCE_MIN_MAX} step={TOLERANCE_MIN_STEP} placeholder={global ? String(secondsToMinutes(global.toleranceAbsoluteS)) : fr.eventSettings.inherit}
              value={settings.toleranceAbsoluteS == null ? "" : secondsToMinutes(settings.toleranceAbsoluteS)} disabled={readOnly}
              onChange={(ev) => setSettings({ ...settings, toleranceAbsoluteS: ev.target.value === "" ? undefined : minutesToSeconds(Number(ev.target.value)) })} />
          </Field>
          <Field label={fr.eventSettings.toleranceRel} hint={global ? fr.eventSettings.globalValue(`${Math.round(global.toleranceRelative * 100)} %`) : fr.eventSettings.inherit}>
            <input type="number" min={0} max={50} step={1} placeholder={global ? String(Math.round(global.toleranceRelative * 100)) : fr.eventSettings.inherit}
              value={settings.toleranceRelative == null ? "" : Math.round(settings.toleranceRelative * 100)} disabled={readOnly}
              onChange={(ev) => setSettings({ ...settings, toleranceRelative: ev.target.value === "" ? undefined : Number(ev.target.value) / 100 })} />
          </Field>
        </div>
        <p className="muted small">{fr.eventSettings.toleranceExample(8, formatDuration(thresholdS(effAbs, effRel, 8 * 60)))}</p>

        <h3>{fr.eventSettings.courses}</h3>
        <p className="muted small">{fr.eventSettings.coursesHelp}</p>
        <p className="muted small">{fr.eventSettings.courseTolerance} : {fr.eventSettings.courseToleranceHelp}</p>
        <div className="grid2">
          <Field label={fr.eventSettings.courseToleranceAbs} hint={fr.eventSettings.globalValue(formatDuration(globalCourse("courseToleranceAbsoluteS")))}>
            <input type="number" min={0} max={COURSE_TOLERANCE_MIN_MAX} step={TOLERANCE_MIN_STEP} placeholder={String(secondsToMinutes(globalCourse("courseToleranceAbsoluteS")))}
              value={settings.courseToleranceAbsoluteS == null ? "" : secondsToMinutes(settings.courseToleranceAbsoluteS)} disabled={readOnly}
              onChange={(ev) => setCourse("courseToleranceAbsoluteS", ev.target.value === "" ? undefined : minutesToSeconds(Number(ev.target.value), COURSE_TOLERANCE_MIN_MAX))} />
          </Field>
          <Field label={fr.eventSettings.courseToleranceRel} hint={fr.eventSettings.globalValue(pct(globalCourse("courseToleranceRelative")))}>
            <input type="number" min={0} max={COURSE_RELATIVE_PCT_MAX} step={1} placeholder={String(Math.round(globalCourse("courseToleranceRelative") * 100))}
              value={settings.courseToleranceRelative == null ? "" : Math.round(settings.courseToleranceRelative * 100)} disabled={readOnly}
              onChange={(ev) => setCourse("courseToleranceRelative", ev.target.value === "" ? undefined : Number(ev.target.value) / 100)} />
          </Field>
        </div>
        <p className="muted small">{fr.eventSettings.courseToleranceExample(8, formatDuration(thresholdS(effCourse("courseToleranceAbsoluteS"), effCourse("courseToleranceRelative"), 8 * 60)))}</p>
        <div className="grid2">
          <Field label={fr.eventSettings.courseCrossing} hint={fr.eventSettings.globalValue(`${Math.round(globalCourse("courseCrossingPenaltyS"))} s`)}>
            <input type="number" min={0} max={COURSE_CROSSING_S_MAX} step={COURSE_CROSSING_S_STEP} placeholder={String(Math.round(globalCourse("courseCrossingPenaltyS")))}
              value={settings.courseCrossingPenaltyS == null ? "" : Math.round(settings.courseCrossingPenaltyS)} disabled={readOnly}
              onChange={(ev) => setCourse("courseCrossingPenaltyS", ev.target.value === "" ? undefined : crossingPenaltyS(Number(ev.target.value)))} />
          </Field>
        </div>
        <p className="muted small">{fr.eventSettings.courseCrossingHelp}</p>

        <h3>{fr.eventSettings.confirmations}</h3>
        <p className="muted small">{fr.eventSettings.confirmationsHelp} {fr.eventSettings.wrongWayHelp}</p>
        <table className="table confirmations small">
          <thead><tr><th>{fr.eventSettings.passage}</th><th>{fr.eventSettings.modeNormal}</th><th>{fr.eventSettings.modeEmergency}</th></tr></thead>
          <tbody>
            {PASSAGE_KINDS.map((k) => (
              <tr key={k}>
                <td>{fr.passageKind[k]}</td>
                {MODES.map((m) => {
                  const label = `${fr.passageKind[k]}, ${m === "normal" ? fr.eventSettings.modeNormal : fr.eventSettings.modeEmergency}`;
                  if (!appliesTo(k, m)) return <td key={m}><select aria-label={label} disabled><option>{fr.eventSettings.notApplicable}</option></select></td>;
                  const v = overrideOf(settings, m, k);
                  const g = global ? (globalFlags[m][FLAG_KEY[k]] ? fr.common.yes : fr.common.no).toLowerCase() : null;
                  return (
                    <td key={m}>
                      <select aria-label={label} value={v == null ? "inherit" : v ? "on" : "off"} disabled={readOnly}
                        onChange={(ev) => setSettings(withOverride(settings, m, k, ev.target.value === "inherit" ? undefined : ev.target.value === "on"))}>
                        <option value="inherit">{g ? fr.eventSettings.inheritValue(g) : fr.eventSettings.inherit}</option>
                        <option value="on">{fr.common.yes}</option>
                        <option value="off">{fr.common.no}</option>
                      </select>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
        <div className="grid2">
          <Field label={fr.eventSettings.confirmationMinGain} hint={fr.eventSettings.globalValue(formatDuration(globalConfirmation("confirmationMinGainS")))}>
            <input type="number" min={0} max={CONFIRMATION_MIN_GAIN_MIN_MAX} step={TOLERANCE_MIN_STEP} placeholder={String(secondsToMinutes(globalConfirmation("confirmationMinGainS")))}
              value={settings.confirmationMinGainS == null ? "" : secondsToMinutes(settings.confirmationMinGainS)} disabled={readOnly}
              onChange={(ev) => setConfirmation("confirmationMinGainS", ev.target.value === "" ? undefined : minutesToSeconds(Number(ev.target.value), CONFIRMATION_MIN_GAIN_MIN_MAX))} />
          </Field>
        </div>
        <p className="muted small">{fr.eventSettings.confirmationMinGainHelp}</p>
        <p className="muted small">{fr.eventSettings.reofferHelp}</p>
        <div className="grid2">
          <Field label={fr.eventSettings.reofferAbs} hint={fr.eventSettings.globalValue(formatDuration(globalConfirmation("reofferGainAbsoluteS")))}>
            <input type="number" min={0} max={REOFFER_ABS_MIN_MAX} step={TOLERANCE_MIN_STEP} placeholder={String(secondsToMinutes(globalConfirmation("reofferGainAbsoluteS")))}
              value={settings.reofferGainAbsoluteS == null ? "" : secondsToMinutes(settings.reofferGainAbsoluteS)} disabled={readOnly}
              onChange={(ev) => setConfirmation("reofferGainAbsoluteS", ev.target.value === "" ? undefined : minutesToSeconds(Number(ev.target.value), REOFFER_ABS_MIN_MAX))} />
          </Field>
          <Field label={fr.eventSettings.reofferRel} hint={fr.eventSettings.globalValue(pct(globalConfirmation("reofferGainRelative")))}>
            <input type="number" min={0} max={REOFFER_REL_PCT_MAX} step={5} placeholder={String(Math.round(globalConfirmation("reofferGainRelative") * 100))}
              value={settings.reofferGainRelative == null ? "" : Math.round(settings.reofferGainRelative * 100)} disabled={readOnly}
              onChange={(ev) => setConfirmation("reofferGainRelative", ev.target.value === "" ? undefined : Math.min(REOFFER_REL_PCT_MAX, Math.max(0, Number(ev.target.value))) / 100)} />
          </Field>
        </div>
        {conflict && (
          <Notice kind="warn">{fr.common.conflict}
            <div className="small">{fr.common.version} {conflict.version}{conflict.updatedBy ? ` ${fr.common.by} ${conflict.updatedBy}` : ""}, {formatDateTime(conflict.updatedAt)}</div>
            <button type="button" onClick={() => { setConflict(null); setError(null); void onChanged(); }}>{fr.common.reloadCurrent}</button>
          </Notice>
        )}
        {error && !conflict && <div className="error small">{error}</div>}
        {!readOnly && <div className="row end"><button type="submit" className="primary" disabled={busy || offline} title={offline ? fr.common.offlineShort : undefined}>{busy ? fr.common.saving : fr.common.save}</button></div>}
      </form>

      <div className="card stack">
        <h3>{fr.eventSettings.zone}</h3>
        <p className="muted small">{fr.eventSettings.zoneHelp}</p>
        {!e.zone && !zonePending && <Notice kind="warn">{fr.eventSettings.noZone}</Notice>}
        {zonePending && (
          <Notice kind="info">{fr.workspace.draw.stop} : {fr.common.apply} ?
            <div className="row">
              <button className="primary" disabled={busy || offline} onClick={async () => { await patch({ zone: zonePending }, fr.eventSettings.saved); onZoneApplied(); }}>{fr.common.apply}</button>
              <button onClick={onCancelZone}>{fr.common.cancel}</button>
            </div>
          </Notice>
        )}
        {!readOnly && !zonePending && (
          <div className="row wrap">
            {zoneDrawing ? <button onClick={onCancelZone}>{fr.workspace.draw.cancel}</button> : <button onClick={onDrawZone}>{e.zone ? fr.eventSettings.redrawZone : fr.eventSettings.drawZone}</button>}
            {e.zone && !zoneDrawing && <button className="ghost danger" onClick={() => setConfirm("clearZone")}>{fr.eventSettings.clearZone}</button>}
          </div>
        )}
      </div>

      <div className="card stack">
        <h3>{fr.workspace.checklist}</h3>
        {e.status === "DRAFT" && checklist && (
          <>
            <Notice kind={checklist.ok ? "ok" : "warn"}>{checklist.ok ? fr.checklist.ok : fr.checklist.blocked}</Notice>
            {checklist.blockers.length > 0 && <ul className="small warn">{checklist.blockers.map((b) => <li key={b}>{fr.errors[b] ?? b}</li>)}</ul>}
            {checklist.validationErrors.length > 0 && <ul className="small warn">{checklist.validationErrors.map((b) => <li key={b}>{b}</li>)}</ul>}
            <ul className="small">
              <li>{fr.checklist.restrictions} : {Object.entries(checklist.restrictionsByKind).map(([k, n]) => `${fr.kind[k] ?? k} ${n}`).join(", ") || fr.common.none}</li>
              <li>{fr.checklist.pois} : {checklist.poiCount} · {fr.checklist.categories} : {checklist.categoryCount}</li>
              <li>{fr.checklist.withoutEnd} : {checklist.restrictionsWithoutEnd}</li>
              <li>{fr.checklist.outside} : {checklist.objectsOutsideZone}</li>
              {checklist.overriddenSettings.length > 0 && <li>{fr.checklist.overridden} : {checklist.overriddenSettings.map((k) => fr.settingsNames[k] ?? k).join(", ")}</li>}
              <li>{fr.checklist.packs} : {checklist.packs.length ? checklist.packs.map((p) => p.name).join(", ") : <span className="warn">{fr.checklist.noPack}</span>}</li>
            </ul>
          </>
        )}
        {e.status === "PUBLISHED" && <p className="small">{fr.events.status.PUBLISHED}{e.publishedAt ? ` · ${formatDateTime(e.publishedAt)}` : ""}. {fr.workspace.diffusion}</p>}
        <div className="row wrap">
          {e.status === "DRAFT" && s.can("event.publish") && <button className="primary" disabled={busy || offline || !checklist?.ok} title={offline ? fr.common.offlineShort : undefined} onClick={() => setConfirm("publish")}>{fr.workspace.publish}</button>}
          {e.status !== "ARCHIVED" && s.can("event.publish") && <button disabled={busy} onClick={() => setConfirm("archive")}>{fr.workspace.archive}</button>}
          {e.status === "ARCHIVED" && s.isAdmin && <button disabled={busy} onClick={() => setConfirm("unarchive")}>{fr.workspace.unarchive}</button>}
          {s.can("data.delete") && <button className="danger ghost" disabled={busy} onClick={() => { setConfirmName(""); setConfirm("delete"); }}>{fr.workspace.deleteEvent}</button>}
        </div>
        {error && conflict == null && confirm == null && <div className="error small">{error}</div>}
      </div>

      <Confirm open={confirm === "publish"} title={fr.workspace.publish} text={activeNow || Date.parse(e.endAt) > Date.now() && Date.parse(e.startAt) < Date.now() ? fr.workspace.publishConfirmActive : `${fr.workspace.publish} « ${e.name} » ?`} confirmLabel={fr.workspace.publish} onConfirm={() => lifecycle("publish")} onClose={() => setConfirm(null)} />
      <Confirm open={confirm === "archive"} title={fr.workspace.archive} text={`${fr.workspace.archive} « ${e.name} » ? ${fr.workspace.archived}`} confirmLabel={fr.workspace.archive} onConfirm={() => lifecycle("archive")} onClose={() => setConfirm(null)} />
      <Confirm open={confirm === "unarchive"} title={fr.workspace.unarchive} text={`${fr.workspace.unarchive} « ${e.name} » ?`} confirmLabel={fr.workspace.unarchive} onConfirm={() => lifecycle("unarchive")} onClose={() => setConfirm(null)} />
      <Confirm open={confirm === "clearZone"} title={fr.eventSettings.clearZone} text={fr.eventSettings.noZone} danger confirmLabel={fr.eventSettings.clearZone} onConfirm={async () => { setConfirm(null); await patch({ clearZone: true }, fr.eventSettings.saved); }} onClose={() => setConfirm(null)} />
      <Confirm open={confirm === "delete"} title={fr.workspace.deleteTitle} text={fr.workspace.deleteText} danger confirmLabel={fr.common.delete} onConfirm={del} onClose={() => setConfirm(null)}>
        <Field label={fr.workspace.deleteName}><input value={confirmName} onChange={(ev) => setConfirmName(ev.target.value)} placeholder={e.name} /></Field>
        {error && <div className="error small">{error}</div>}
      </Confirm>
    </section>
  );
}
